import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Divider, Form, Input, InputProps, Popover, Row, Select, Spin, Table, TreeSelect } from 'antd';
import { wdiCore } from './wdiCore';
import { FieldProps } from './FieldProps';
import type { SelectProps } from 'antd/es/select';
import { TreeSelectProps } from 'antd/es/tree-select';
import DataStore from '../GridWidget/DataStore';
import { CloseOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import {WdiInput, WdiModalAppProps} from '@/components/WdiControls/index';
import { WdiModal } from '@/components/WdiControls/WdiModalForm';
import { ResponseCode } from '@/utils/response'; /// remote 根据录入内容实时搜索

/// remote 根据录入内容实时搜索
/// reloadKey 改变之后重新加载数据
/// dataLoading
/// antdProps: render中antd属性会覆盖掉默认属性
export type WdiSelectProps = {
  label?: string;
  remote?: boolean;
  reloadKey?: string | number;
  dataLoading?: (params: any) => Promise<any[]>;
} & SelectProps;

export const getWdiSelectDefaultProps = (props: WdiSelectProps) => {
  const defaultProps: WdiSelectProps = {
    maxTagCount: 'responsive'
  };
  defaultProps.showSearch = true;
  defaultProps.showArrow = false;
  if (props.remote) {
    defaultProps.filterOption = false;
  } else {
    defaultProps.filterOption = (inputValue, option) => {
      return !!(option && wdiCore.String.Match(option.label, inputValue));
    }
  }
  if (props.mode === 'multiple') {
    defaultProps.autoClearSearchValue = false;
    defaultProps.maxTagPlaceholder = FieldProps.Select.createTooltipMaxTagPlaceholder();
    defaultProps.tagRender = FieldProps.Select.createTooltipTagRender();
  } else {
    defaultProps.allowClear = true;
  }
  if (props.value) {
    defaultProps.value = props.value;
  }
  if (wdiCore.Object.IsNullOrUndefined(props.dropdownMatchSelectWidth)) {
    defaultProps.dropdownMatchSelectWidth = false;
  }
  return defaultProps;
};

const timerRef: any = {
  current: {
    value: null,
    timerID: null
  }
};

export namespace WdiSelectFormat {
  export const getvalue = (value: any) => {
    if (wdiCore.Object.IsArray(value)) {
      return value.map((item: any) => {
        if (wdiCore.Object.IsObject(item)) {
          return item.value;
        }
        return item;
      }).join();
    } else if (wdiCore.Object.IsObject(value)) {
      return value['value'];
    } else {
      return value;
    }
  };
  export const gettext = (value: any) => {
    if (wdiCore.Object.IsArray(value)) {
      return value.map((item: any) => item.label).join();
    } else if (wdiCore.Object.IsObject(value)) {
      return value['label'];
    } else {
      return value;
    }
  };
}

/// remote
/// reloadKey
/// dataLoading
export const WdiSelect: React.FC<WdiSelectProps> = (props) => {
  const {
    remote,
    reloadKey,
    dataLoading,
    value,
    label,
    onChange,
    ...newProps
  } = Object.assign({}, getWdiSelectDefaultProps(props), props);
  const [options, setOptions] = useState<any[]>([]);
  const [selectVal, setSelectVal] = useState<any>(value);

  const selectRef = useRef<{
    inputValue: string,
    selectedOptions: any[]
  }>({inputValue: "", selectedOptions: []});

  const handleComboboxRemoteSearch = useMemo(() => {
    return (inputValue: any) => {
      if (timerRef.current && timerRef.current.timerID) {
        clearTimeout(timerRef.current.timerID);
        timerRef.current.timerID = null;
      }
      if (timerRef.current) {
        timerRef.current.value = inputValue;
        const fetchComboboxRemote = () => {
          selectRef.current.inputValue = inputValue;
          if (dataLoading && remote) {
            const params: any = {top: 100};
            if (!wdiCore.String.IsNullOrEmpty(inputValue) && inputValue.indexOf('\'') == -1) {
              params.filterValue = inputValue;
            }
            dataLoading(params).then((items: any) => {
              if (wdiCore.String.EqIngoreCase(inputValue, timerRef.current.value)) {
                const mergedMap = new Map([...items].map(obj => [JSON.stringify(obj), obj]));
                const opts = Array.from(mergedMap.values());
                setOptions(opts);
              }
            });
          }
        };
        timerRef.current.timerID = window.setTimeout(fetchComboboxRemote, 300);
      }
    };
  }, [reloadKey, remote]);

  const dropdownRender = (menu: ReactNode) => {
    return <div>
      <div style={{padding: '4px 8px 8px 8px', cursor: 'pointer'}}>
        <Button size="small" onClick={() => {
          const mappingOpts = options.filter((m: any) => (wdiCore.String.Match(m.label, selectRef.current.inputValue)));
          const mergedMap = new Map([...selectRef.current.selectedOptions, ...mappingOpts].map(obj => [JSON.stringify(obj), obj]));
          selectRef.current.selectedOptions = Array.from(mergedMap.values());
          setSelectVal(selectRef.current.selectedOptions.map((m: any) => m.value));
          onChange?.(selectRef.current.selectedOptions.map((m: any) => m.value), selectRef.current.selectedOptions);
          handleComboboxRemoteSearch(selectRef.current.inputValue);
        }}>
          全选
        </Button>
        <Button style={{margin: '0 10px'}} size="small" onClick={() => {
          selectRef.current.selectedOptions = [];
          setSelectVal([]);
          onChange?.([], []);
        }}>
          取消
        </Button>
      </div>
      <Divider style={{margin: '0'}}/>
      {menu}
    </div>
  }

  useEffect(() => {
    //解决组件卸载后更新问题
    let unmounted = false;
    if (dataLoading) {
      const params: any = {};
      if (remote) {
        params.top = 100;
      }
      dataLoading(params).then((items: any) => {
        if (unmounted) {
          return;
        }
        setOptions(items);
      });
    } else if (props.options) {
      setOptions(props.options);
    }

    return () => {
      unmounted = true;
    };
  }, [reloadKey, remote, props.options])

  useEffect(() => {
    setSelectVal(value);
  }, [value]);

  const antdProps = Object.assign({options}, newProps);
  antdProps.onSearch = handleComboboxRemoteSearch;

  antdProps.onClear = () => {
    newProps?.onClear?.();
    handleComboboxRemoteSearch("");
  }
  antdProps.onBlur = (e) => {
    newProps?.onBlur?.(e);
    handleComboboxRemoteSearch("");
  }
  if (antdProps.mode == 'multiple') {
    antdProps.dropdownRender = dropdownRender;
  }
  if (!antdProps.placeholder) {
    antdProps.placeholder = "请选择" + (label || "");
  }
  const onChangeInternal = (value: any, option: any | any[]) => {
    if (wdiCore.Object.IsNullOrUndefined(option)) {
      selectRef.current.selectedOptions = [];
    } else if (wdiCore.Object.IsArray(option)) {
      selectRef.current.selectedOptions = option;
    } else {
      selectRef.current.selectedOptions = [option];
    }
    setSelectVal(value);
    onChange?.(value, option);
  }
  return (
    <Select {...antdProps} value={selectVal} onChange={onChangeInternal}/>
  )
};

export type WdiTreeSelectProps = {
  label?: string;
  reloadKey?: any;
  dataLoading?: (params: any) => Promise<any>;
  onExSelect?: (value: any, node: any, extra: any) => void;
  defaultExpandKeys?: any[];
  ref?: any;
  // 搜索框输入多少字符再检索，默认2个
  filterKeyLength?: number;
  //是否展开默认数据
  isExpanded?: boolean;
} & TreeSelectProps;

export const getWdiTreeSelectDefaultProps = (props: WdiTreeSelectProps) => {
  const defaultProps: WdiTreeSelectProps = {
    maxTagCount: 'responsive',
    treeNodeFilterProp: 'filterKey',
    showSearch: true,
    showArrow: false,
    autoClearSearchValue: false,
    isExpanded: true
  };
  if (props.filterTreeNode) {
    defaultProps.filterTreeNode = props.filterTreeNode;
  } else {
    const filterKeyLength = props.filterKeyLength || 2;
    defaultProps.filterTreeNode = (value, treeNode) => {
      if (value.length >= filterKeyLength) {
        return treeNode.filterKey?.includes(value);
      }
      return false;
    }
  }
  if (props.multiple) {
    defaultProps.treeCheckStrictly = true;
    defaultProps.treeCheckable = true;
    defaultProps.maxTagPlaceholder = FieldProps.Select.createTooltipMaxTagPlaceholder();
  } else {
    defaultProps.allowClear = true;
  }
  if (wdiCore.Object.IsNullOrUndefined(props.dropdownMatchSelectWidth)) {
    defaultProps.dropdownMatchSelectWidth = false;
  }
  return defaultProps;
}

export const WdiTreeSelect: React.FC<WdiTreeSelectProps> = React.forwardRef((props, ref) => {
  const { reloadKey, dataLoading, onExSelect, defaultExpandKeys, isExpanded, label, ...newProps } =
    Object.assign({}, getWdiTreeSelectDefaultProps(props), props);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [dict, setDict] = useState<any>({});
  const [treeDefaultExpandedKeys, setTreeDefaultExpandedKeys] = useState<any[]>([]);

  //导出ref 获取内部数据
  React.useImperativeHandle(ref, () => ({
    getTreeData: () => {
      return treeData;
    },
    getDict: () => {
      return dict;
    },
  }));

  useEffect(() => {
    //解决组件卸载后更新问题
    //devScripts.js:6523 Warning: Can't perform a React state update on an unmounted component.
    //This is a no-op, but it indicates a memory leak in your application.
    //To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
    let unmounted = false;
    (async () => {
      if (dataLoading) {
        const data = await dataLoading({});
        if (unmounted) {
          return;
        }

        setTreeData(data.treeData);
        setTreeDefaultExpandedKeys(FieldProps.TreeSelect.getTreeExpandedKeys(data.treeData));
        setDict(data.dict);
      } else if (props.treeData) {
        if (unmounted) {
          return;
        }

        setTreeDefaultExpandedKeys(FieldProps.TreeSelect.getTreeExpandedKeys(props.treeData));
        setDict(FieldProps.TreeSelect.buildDictMap(props.treeData));
      }
      if (!isExpanded) {
        setTreeDefaultExpandedKeys([]);
      }
    })();
    return () => {
      unmounted = true;
    };
  }, [reloadKey, props.treeData]);

  const antdProps = Object.assign(
    {
      treeData,
      treeDefaultExpandedKeys:
        defaultExpandKeys && defaultExpandKeys.length > 0
          ? defaultExpandKeys
          : treeDefaultExpandedKeys,
    },
    newProps,
  );
  if (onExSelect) {
    antdProps.onSelect = (value: any, node: any) => {
      if (onExSelect) {
        onExSelect(value, node, dict);
      }
    };
  }
  if (!antdProps.placeholder) {
    antdProps.placeholder = '请选择' + (label || '');
  }
  return <TreeSelect {...antdProps} />;
});

type SelectParseModalProps = {
  dataSource?: any[];
  dataLoading?: (params: any) => Promise<any>;
  optionFilter?: (item: any, options: any[]) => any;
} & WdiModalAppProps;

export const SelectParseModalApp: React.FC<SelectParseModalProps> = (props) => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [optionValueMap] = useState(new Map());
  const [dataSourceLoading, setDataSourceLoading] = useState(true);

  const columns: ColumnsType<any> = [
    {
      title: (
        <CloseOutlined
          onClick={() => {
            if (window.confirm('确定要删除所有行吗？')) {
              setDataSource([]);
            }
          }}
        />
      ),
      align: 'center',
      width: 50,
      dataIndex: 'delete',
      ellipsis: true,
      render: (_, record) => (
        <CloseOutlined
          onClick={() => {
            setDataSource(DataStore.del(dataSource, record.uid));
          }}
        />
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      ellipsis: true,
      render: (value, record) => <span style={record.isRed ? { color: 'red' } : {}}>{value}</span>,
    },
  ];

  const buildDataSource = (value: string) => {
    const result = value
      .replaceAll(/\r\n/g, ',')
      .replaceAll(/\n/g, ',')
      .replaceAll('，', ',')
      .replaceAll(';', ',')
      .replaceAll('；', ',');
    const resultList = result.split(',').filter((v) => v != '');
    const newDataSource: any[] = [];
    resultList.forEach((item) => {
      let option;
      if (props.optionFilter) {
        option = props.optionFilter(item, options);
      } else {
        option = options.find(
          (option) => option.text == item || option.value == item || option.label == item,
        );
      }
      if (option) {
        newDataSource.push({
          content: option.label,
          isRed: false,
          value: option.value,
          label: option.label,
        });
      } else {
        newDataSource.push({ content: item, isRed: true, value: '' });
      }
    });
    setDataSource(DataStore.addMulti(dataSource, newDataSource));
    form.setFieldsValue({
      content: '',
    });
  };

  const handelParse = (e: any) => {
    setLoading(true);
    setTimeout(() => {
      let value = e.target.value;
      if (value) {
        buildDataSource(value);
      }
      setLoading(false);
    }, 100);
  };

  const handelEnter = (e: any) => {
    if ('enter' == e?.key.toLocaleLowerCase()) {
      if ('textarea' == e.target?.tagName.toLocaleLowerCase()) {
        setLoading(true);
        setTimeout(() => {
          let value = e.target.value;
          if (value) {
            buildDataSource(value);
          }
          setLoading(false);
        }, 100);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (props.dataLoading) {
      props.dataLoading({}).then((items) => {
        optionValueMap.clear();
        items.forEach((item: any) => {
          optionValueMap.set(item.value, item);
        });
        setOptions(items);
        setDataSourceLoading(false);
      });
    }
  }, [props.dataLoading]);

  useEffect(() => {
    if (!options || options.length == 0) return;
    if (!dataSource) return;
    if (dataSource.length > 0) return;
    setDataSource(
      DataStore.addMulti(
        dataSource,
        props.dataSource?.map((item: any) => {
          const option = optionValueMap.get(item.value || item);
          if (option) {
            return {
              content: option.label,
              isRed: false,
              value: option.value,
              label: option.label,
            };
          } else {
            return { content: item.label, isRed: true, value: '' };
          }
        }),
      ),
    );
  }, [options, props.dataSource]);

  return (
    <>
      <WdiModal
        title={props.title}
        visible={props.visible}
        width={800}
        onCancel={() => {
          props.onClose?.();
        }}
        onOk={() => {
          props.onSave?.({
            code: ResponseCode.SUCCESS,
            data: [...new Set(dataSource.filter((item) => item.value != ''))],
          });
        }}
      >
        <Spin tip="识别中，请稍候" spinning={loading}>
          <Form form={form}>
            <Row>
              <Col span={24}>
                <Spin tip="正在加载数据，请稍候" spinning={dataSourceLoading}>
                  <Form.Item name="content" label="粘贴至此">
                    <Input.TextArea onPaste={handelParse} onKeyUp={handelEnter} />
                  </Form.Item>
                </Spin>
              </Col>
            </Row>
          </Form>
          <Table columns={columns} size="small" dataSource={dataSource} pagination={false} />
        </Spin>
      </WdiModal>
    </>
  );
};

type WdiPanelSelectProps = {
  value?: any;
  onChange?: (value: any) => void;
  splitSize: number;
  dataSource: string[]
} & InputProps

//下拉面板 类似日期选择面板选择
export const WdiPanelSelect: React.FC<WdiPanelSelectProps> = (props) => {
  const [popOverModalProps, setPopOverModalProps] = React.useState<WdiModalAppProps>();
  const [value, setValue] = useState<string>();
  const innerProps = {...props}
  delete innerProps.value;
  if (props.suffix) {
    innerProps.suffix = <div style={{cursor: 'pointer'}} onClick={() => setPopOverModalProps({visible: true})}>
      {props.suffix}
    </div>
  }
  useEffect(() => {
    if (wdiCore.String.IsNullOrEmpty(props.value) || !props.dataSource.includes(props.value)) {
      setValue(undefined);
      return;
    }
    setValue(props.value);
  }, [props.value])


  return (<>
    <Popover
      onVisibleChange={(visible) => {
        if (!visible) {
          setPopOverModalProps({visible})
        }
      }}
      trigger="click"
      style={{width: '100%'}}
      placement={"bottomLeft"}
      visible={popOverModalProps?.visible}
      content={() => {
        const maxRow = Math.ceil(props.dataSource.length / props.splitSize);
        const colSpan = Math.floor(24 / props.splitSize);
        const rowResult = [];
        for (let i = 0; i < maxRow; i++) {
          const colResult = [];
          for (let j = i * props.splitSize; j < (i + 1) * props.splitSize; j++) {
            colResult.push(<Col span={colSpan}>
              <Button
                type={"text"}
                onClick={(e) => {
                  setPopOverModalProps({visible: false})
                  props.onChange?.(e.currentTarget.innerText)
                }}
              >
                {props.dataSource[j]}
              </Button>
            </Col>)
            if (j == props.dataSource.length - 1) {
              break;
            }
          }
          rowResult.push(<Row key={i}>{colResult}</Row>)
        }
        return rowResult;
      }}
    >
      <WdiInput
        {...innerProps}
        value={value}
        onChange={(e) => {
          props.onChange?.(e.target.value);
        }}
        onClick={() => setPopOverModalProps({visible: true})}
      />
    </Popover>
  </>)


}
